import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Users, FileText, Wrench, BookOpen, BarChart3, Settings, Menu, X } from 'lucide-react';
import axios from 'axios';
import RoundStatusBar from './RoundStatusBar';
import NotificationBell from './NotificationBell';
import { normalizeImageUrl } from '../utils/imageUrl';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Navbar = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [navbarLogo, setNavbarLogo] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/system/settings`).then(res => {
      if (res.data?.navbar_logo) setNavbarLogo(res.data.navbar_logo);
    }).catch(() => {});
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const links = [
    { to: '/dashboard', icon: Trophy, label: t('dashboard'), testId: 'nav-dashboard-link' },
    { to: '/leaderboard', icon: Users, label: t('leaderboard'), testId: 'nav-leaderboard-link' },
    { to: '/race-results', icon: FileText, label: t('results'), testId: 'nav-race-results-link' },
    { to: '/workshop', icon: Wrench, label: t('workshop'), testId: 'nav-workshop-link' },
    { to: '/rules', icon: BookOpen, label: t('rules'), testId: 'nav-rules-link' },
    { to: '/round-results', icon: BarChart3, label: t('standings'), testId: 'nav-round-results-link' },
    { to: '/settings', icon: Settings, label: t('settings'), testId: 'nav-settings-link' },
  ];

  return (
    <>
      <RoundStatusBar />
      <nav className="glass-effect border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/dashboard" className="flex items-center space-x-3 shrink-0">
              {navbarLogo ? (
                <img src={normalizeImageUrl(navbarLogo)} alt="Logo" className="h-10 sm:h-14 max-w-[160px] sm:max-w-[240px] object-contain" data-testid="navbar-logo-img" />
              ) : (
                <>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary flex items-center justify-center font-heading font-black text-lg sm:text-xl">
                    M
                  </div>
                  <span className="font-heading font-black text-xl sm:text-2xl tracking-tight">
                    MSF <span className="text-primary">RACING</span>
                  </span>
                </>
              )}
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center space-x-5 font-nav">
              {links.map(({ to, icon: Icon, label, testId }) => (
                <Link key={to} to={to} data-testid={testId} className={`transition-colors flex items-center space-x-1.5 text-sm ${location.pathname === to ? 'text-primary' : 'text-zinc-400 hover:text-white'}`}>
                  <Icon size={18} /><span>{label}</span>
                </Link>
              ))}
              <NotificationBell />
            </div>

            {/* Mobile controls */}
            <div className="flex lg:hidden items-center gap-2">
              <NotificationBell />
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
                className="p-2 text-zinc-400 hover:text-white transition-colors"
              >
                {mobileOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-zinc-800 bg-zinc-950/95 backdrop-blur-sm" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-1">
              {links.map(({ to, icon: Icon, label, testId }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`mobile-${testId}`}
                  className={`flex items-center space-x-3 px-3 py-2.5 transition-colors ${location.pathname === to ? 'text-primary bg-primary/10' : 'text-zinc-300 hover:text-white hover:bg-zinc-800/50'}`}
                >
                  <Icon size={20} />
                  <span className="font-nav text-sm">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
